import { pluralize } from 'humanize-plus';

import {
  type PartnerWithTagsType,
  type TagEditType,
  buildBulkTagItems,
} from '@/components/partners/bulkTagEdit';

import { TAG_LIMIT_REACHED_FLASH } from '@/constants/partners';
import { defaultErrorMessage } from '@/errors';
import { useFlashesStore, usePartnersStore } from '@/stores';

const BAD_REQUEST_STATUS = 400;

function failureFlash(
  { status, messages }: { status: number | null; messages: string[] },
  savedCount: number,
  partnerCount: number,
) {
  const isTagLimit = status === BAD_REQUEST_STATUS;
  const message = isTagLimit
    ? TAG_LIMIT_REACHED_FLASH.message
    : messages.join(',') || defaultErrorMessage;
  if (savedCount)
    return {
      message,
      description: `Tags were saved for ${savedCount} of ${partnerCount} partners. Save again to update the rest.`,
    };
  if (isTagLimit) return TAG_LIMIT_REACHED_FLASH;
  return { message, description: 'Tags not saved for the selected partners' };
}

/**
 * Saves the Edit Tags modal's edit for every selected partner and reports the
 * outcome in a flash.
 *
 * @param partners - The partners selected when the modal opened
 * @param edit - Labels removed from and added to every partner
 * @returns Whether every partner was saved
 */
export async function saveBulkTagEdit(
  partners: PartnerWithTagsType[],
  edit: TagEditType,
): Promise<boolean> {
  const partnersStore = usePartnersStore();
  const { addSuccessFlash, addErrorFlash } = useFlashesStore();

  const { savedCount, failure } = await partnersStore.bulkEditTags(
    buildBulkTagItems(partners, edit),
  );

  if (failure) {
    addErrorFlash(failureFlash(failure, savedCount, partners.length));
    return false;
  }

  addSuccessFlash({
    message: `Tags updated for ${savedCount} ${pluralize(savedCount, 'partner')}`,
  });
  return true;
}
